import type { ClientFlowAction } from '~/graphql/types.client';

/**
 * Removes secondary parents that are IfElse actions from cloned branch actions.
 *
 * Secondary IfElse parents cannot be preserved on pasted clones because
 * the external IfElse still points to the original child via
 * trueChildId/falseChildId. Keeping the parentId would turn the clone
 * into an extra child of that IfElse.
 */
const stripInvalidSecondaryIfElseParents = (
  branch: Array<ClientFlowAction>,
  allActions: Array<ClientFlowAction>,
): Array<ClientFlowAction> =>
  branch.map(action => {
    if (!('parentIds' in action)) return action;
    if (action.parentIds.length <= 1) return action;

    const nextParentIds = action.parentIds.filter((parentId, index) => {
      // Leave primary parent as is
      if (index === 0) return true;

      const externalParent = allActions.find(
        candidate => candidate.id === parentId,
      );

      // Unknown parent: we cannot determine its type, so keep it to avoid
      // creating orphaned references. If we threw an error here,
      // any paste involving an internal portal relationship would crash
      if (!externalParent) return true;

      return externalParent.__typename !== 'FlowV2_Action_IfElse';
    });

    return nextParentIds.length === action.parentIds.length
      ? action
      : ({ ...action, parentIds: nextParentIds } as ClientFlowAction);
  });

export default stripInvalidSecondaryIfElseParents;
